/**
 * UnifiedDashboardsTable — Details view for the unified Dashboards page.
 *
 * Renders the user's dashboards ("My Dashboard") and the curated prebuilt
 * templates ("Prebuilt") in ONE table. Rows arrive pre-filtered, pre-sorted
 * and pre-paged from the Dashboards orchestrator as `{ kind, item }` pairs so
 * this component stays presentation-only. Every action re-uses the handlers
 * the old tab components used (openDashboard / del / clone / preview / ...),
 * and per-row permissions come from the same flags (can_edit / can_delete /
 * canWriteDashboard / prebuiltLocked / isSuperAdmin).
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    ExternalLink, Pencil, Trash2, Eye, Check, Lock, User, Layers, Database, Plus, MoreVertical,
    Library, LayoutDashboard, Loader2,
} from "lucide-react";
import ReadinessBadge from "@/components/dashboards/ReadinessBadge";
import { IconPlate } from "./MyDashboardsTab";

export function TypePill({ kind, testId }) {
    if (kind === "my") {
        return (
            <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-indigo-500/20 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-xs font-medium whitespace-nowrap"
                data-testid={testId}>
                <User className="h-3.5 w-3.5" /> My Dashboard
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-primary/20 bg-primary/10 text-primary text-xs font-medium whitespace-nowrap"
            data-testid={testId}>
            <Layers className="h-3.5 w-3.5" /> Prebuilt
        </span>
    );
}

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");
const actBtn = "rounded-md h-8 px-3 text-xs gap-1.5 bg-card";

function MyDashboardRow({ d, ctx }) {
    const { canWriteData, canWriteDashboard, openDashboard, setTileSourceTarget, del } = ctx;
    const canEdit = d.can_edit !== undefined ? d.can_edit : canWriteDashboard;
    const canDelete = d.can_delete !== undefined ? d.can_delete : canWriteDashboard;
    const hasSources = Boolean((d.attached_sources?.length || 0) || (d.bind_to_models?.length || 0));
    return (
        <tr className="border-t border-border hover:bg-secondary/30 cursor-pointer transition-colors"
            onClick={() => openDashboard(d)} data-testid={`my-dashboard-row-${d.id}`}>
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                    <IconPlate name={d.name} category={d.category} sourceLabel={d.source_label} size="sm" />
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-foreground truncate">{d.name}</span>
                            {d.is_unified && (
                                <Badge variant="outline" className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5" data-testid={`my-dashboard-unified-${d.id}`}>
                                    <Layers className="h-2.5 w-2.5" /> UNIFIED
                                </Badge>
                            )}
                            {canWriteData && d.needs_source && (
                                <Badge variant="outline"
                                    className="font-mono text-[9px] border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 gap-0.5 cursor-pointer hover:bg-amber-500/20 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); setTileSourceTarget({ id: d.id, name: d.name }); }}
                                    title="Connect data — pick an existing source or create a new one"
                                    data-testid={`my-dashboard-connect-data-${d.id}`}>
                                    <Database className="h-2.5 w-2.5" /> Connect data
                                </Badge>
                            )}
                            {canWriteData && !d.needs_source && hasSources && (
                                <Badge variant="outline"
                                    className="font-mono text-[9px] border-primary/30 bg-primary/10 text-primary gap-0.5 cursor-pointer hover:bg-primary/20 transition-colors"
                                    onClick={(e) => { e.stopPropagation(); setTileSourceTarget({ id: d.id, name: d.name }); }}
                                    title="Add another data source — pick an existing one or create new"
                                    data-testid={`my-dashboard-add-source-${d.id}`}>
                                    <Plus className="h-2.5 w-2.5" /> Add source
                                </Badge>
                            )}
                        </div>
                        {d.description && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{d.description}</div>}
                    </div>
                </div>
            </td>
            <td className="px-4 py-2.5" title={d.category ? `Category: ${d.category}` : undefined}>
                <TypePill kind="my" testId={`dashboard-type-${d.id}`} />
            </td>
            <td className="px-4 py-2.5 font-mono text-xs">{d.widgets?.length || 0}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">v{d.version ?? 1}</td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-[160px]" title={d.created_by_email || ""}>{d.created_by_name || d.created_by_email || "—"}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{fmtDate(d.created_at)}</td>
            <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className={actBtn} onClick={() => openDashboard(d)} data-testid={`my-dashboard-open-${d.id}`}>
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                    </Button>
                    {canEdit && (
                        <Button variant="outline" size="sm" className={actBtn} onClick={() => openDashboard(d)}
                            title="Open in the editor — rename, add widgets and sources"
                            data-testid={`my-dashboard-edit-${d.id}`}>
                            <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                    )}
                    {canDelete && (
                        <Button variant="ghost" size="sm" className="rounded-md h-8 w-8 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => del(d.id, d.name)} title="Delete dashboard"
                            data-testid={`my-dashboard-delete-${d.id}`}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </td>
        </tr>
    );
}

export function TemplateMoreMenu({ t, ctx, onConnectData, triggerClassName = "rounded-md h-8 w-8 p-0 text-muted-foreground hover:text-foreground" }) {
    const { isSuperAdmin, delTemplateInstance, hideTemplate, savePlaybookFromTemplate } = ctx;
    const isModelBound = t._model_bound;
    const isInstantiated = !!t.instance;
    const items = [];
    if ((isModelBound || isInstantiated) && onConnectData) items.push("connect");
    if (isSuperAdmin) items.push("playbook");
    if (isModelBound && isInstantiated) items.push("del-instance");
    if (isSuperAdmin && t.slug) items.push("del-template");
    if (items.length === 0) return null;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className={triggerClassName}
                    onClick={(e) => e.stopPropagation()} data-testid={`template-more-${t.id}`} title="More actions">
                    <MoreVertical className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
                {items.includes("connect") && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onConnectData(t); }} data-testid={`template-connect-data-${t.id}`}>
                        <Database className="h-3.5 w-3.5 mr-2" /> Connect data
                    </DropdownMenuItem>
                )}
                {items.includes("playbook") && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); savePlaybookFromTemplate(t); }} data-testid={`template-save-playbook-${t.id}`}>
                        <Library className="h-3.5 w-3.5 mr-2" /> Save as playbook
                    </DropdownMenuItem>
                )}
                {(items.includes("del-instance") || items.includes("del-template")) && (items.includes("connect") || items.includes("playbook")) && <DropdownMenuSeparator />}
                {items.includes("del-instance") && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); delTemplateInstance(t); }}
                        className="text-destructive focus:text-destructive" data-testid={`template-delete-${t.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete instance
                    </DropdownMenuItem>
                )}
                {items.includes("del-template") && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); hideTemplate(t); }}
                        className="text-destructive focus:text-destructive" data-testid={`template-hide-${t.slug}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete template
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function TemplateRow({ t, ctx }) {
    const { nav, clone, prebuiltLocked, openUpgrade, onConnectData } = ctx;
    const isInstantiated = !!t.instance;
    const preview = () => nav(`/dashboards/preview/${t.id}`);
    return (
        <tr className="border-t border-border hover:bg-secondary/30 cursor-pointer transition-colors"
            onClick={preview} data-testid={`template-row-${t.id}`}>
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                    <IconPlate name={t.name} category={t.category} size="sm" />
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-foreground truncate">{t.name}</span>
                            {t.readiness && <ReadinessBadge readiness={t.readiness} compact data-testid={`template-readiness-${t.id}`} />}
                        </div>
                        {t.description && <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{t.description}</div>}
                    </div>
                </div>
            </td>
            <td className="px-4 py-2.5" title={t.category ? `Category: ${t.category}` : undefined}>
                <TypePill kind="prebuilt" testId={`dashboard-type-${t.id}`} />
            </td>
            <td className="px-4 py-2.5 font-mono text-xs">{t.widgets?.length || 0}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground" data-testid={`template-version-${t.id}`}>v{t.version ?? 1}</td>
            <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-[160px]" title={t.created_by_email || ""} data-testid={`template-created-by-${t.id}`}>{t.created_by_name || t.created_by_email || "—"}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground" data-testid={`template-created-at-${t.id}`}>{fmtDate(t.created_at)}</td>
            <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className={actBtn} onClick={preview}
                        title="Preview this template with sample data" data-testid={`template-preview-${t.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Preview
                    </Button>
                    {prebuiltLocked && !isInstantiated ? (
                        <Button variant="outline" size="sm" className={actBtn} onClick={() => openUpgrade?.()}
                            title="Prebuilt dashboards aren't included in your plan."
                            data-testid={`template-clone-${t.id}-locked`}>
                            <Lock className="h-3.5 w-3.5" /> Upgrade
                        </Button>
                    ) : isInstantiated ? (
                        <Button variant="outline" size="sm" className={actBtn} onClick={() => clone(t)}
                            title="Open your dashboard created from this template" data-testid={`template-clone-${t.id}`}>
                            <ExternalLink className="h-3.5 w-3.5" /> Open
                        </Button>
                    ) : (
                        <Button size="sm" className="rounded-md h-8 px-3.5 text-xs gap-1.5 shadow-sm" onClick={() => clone(t)}
                            title="Create a dashboard from this template" data-testid={`template-clone-${t.id}`}>
                            <Check className="h-3.5 w-3.5" /> Use
                        </Button>
                    )}
                    <TemplateMoreMenu t={t} ctx={ctx} onConnectData={onConnectData} />
                </div>
            </td>
        </tr>
    );
}

export default function UnifiedDashboardsTable({ rows, ctx, loading, emptyLabel }) {
    return (
        <div className="border border-border rounded-xl bg-card overflow-x-auto shadow-sm" data-testid="dashboards-unified-table">
            <table className="w-full text-sm min-w-[1040px]">
                <thead className="bg-secondary/40">
                    <tr>
                        <th className="text-left px-4 py-3 eyebrow">Template / Name</th>
                        <th className="text-left px-4 py-3 eyebrow w-40">Category</th>
                        <th className="text-left px-4 py-3 eyebrow w-24">Widgets</th>
                        <th className="text-left px-4 py-3 eyebrow w-20">Version</th>
                        <th className="text-left px-4 py-3 eyebrow w-40">Created by</th>
                        <th className="text-left px-4 py-3 eyebrow w-28">Created</th>
                        <th className="text-left px-4 py-3 eyebrow w-[250px]">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && rows.length === 0 ? (
                        <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="dashboards-loading">
                            <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin opacity-60" /> Loading dashboards…
                        </td></tr>
                    ) : rows.length === 0 ? (
                        <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="dashboards-empty">
                            <LayoutDashboard className="h-8 w-8 mx-auto mb-2 opacity-30" />
                            {emptyLabel || "No dashboards match your filter."}
                        </td></tr>
                    ) : rows.map(({ kind, item }) => (kind === "my"
                        ? <MyDashboardRow key={`my-${item.id}`} d={item} ctx={ctx} />
                        : <TemplateRow key={`tpl-${item.id}`} t={item} ctx={ctx} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
